import {
  Box,
  Stack,
  Typography,
  IconButton,
  Divider,
  Button,
  Collapse,
  TableCell,
  TableRow,
  Table,
  TableHead,
  TableBody,
  TableContainer,
  Paper,
  Tooltip,
  Chip,
} from "@mui/material";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import moment from "moment";

interface LeadNote {
  id: string;
  method: string;
  summary: string;
  outcome: string;
  communicationDate: string;
  followUpDate: Date;
}

interface Lead {
  id: string;
  notes?: LeadNote[];
}

interface LeadNotesRowProps {
  lead: Lead;
  expandedRow: string | null;
  handleAddNote: (lead: Lead) => void;
  handleEditNote: (note: LeadNote) => void;
  handleDeleteNote: (note: LeadNote) => void;
  colSpan?: number;
}

const LeadNotesRow: React.FC<LeadNotesRowProps> = ({
  lead,
  expandedRow,
  handleAddNote,
  handleEditNote,
  handleDeleteNote,
  colSpan = 7,
}) => {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ p: 0, border: "none" }}>
        <Collapse in={expandedRow === lead.id} timeout="auto" unmountOnExit>
          <Box sx={{ p: 2, backgroundColor: "#f9fafb" }}>
            {/* Header row */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Notes
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<StickyNote2OutlinedIcon />}
                onClick={() => handleAddNote(lead)}
                sx={{
                  borderRadius: 2,
                  py: 0.5,
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  textTransform: "none",
                }}
              >
                Add Note
              </Button>
            </Stack>
            <Divider sx={{ mb: 2 }} />

            {/* Notes Table */}
            {lead.notes && lead.notes.length > 0 ? (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  boxShadow: 2,
                  overflowX: "auto",
                }}
              >
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#f4f6f8" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, border: "1px solid #e5e7eb" }}
                      >
                        Outcome
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, border: "1px solid #e5e7eb" }}
                      >
                        Summary
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, border: "1px solid #e5e7eb" }}
                      >
                        Follow Up
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, border: "1px solid #e5e7eb" }}
                      >
                        Date
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, border: "1px solid #e5e7eb" }}
                        align="center"
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lead.notes.map((note, index) => (
                      <TableRow
                        key={note.id}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "white" : "#fafafa",
                          "&:hover": {
                            backgroundColor: "#f1f5f9",
                          },
                          transition: "all 0.2s ease-in-out",
                        }}
                      >
                        {/* Method as pill */}
                        <TableCell>
                          <Chip
                            label={note.method}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>

                        {/* Outcome as pill */}
                        <TableCell sx={{ border: "1px solid #e5e7eb" }}>
                          <Chip
                            label={note.outcome}
                            size="small"
                            color={
                              note.outcome.toLowerCase().includes("success")
                                ? "success"
                                : note.outcome.toLowerCase().includes("fail")
                                ? "error"
                                : "default"
                            }
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>

                        {/* Summary */}
                        <TableCell
                          sx={{ maxWidth: 300, border: "1px solid #e5e7eb" }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                          >
                            {note.summary}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ border: "1px solid #e5e7eb" }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {note?.followUpDate ? moment(note.followUpDate).format("MM/DD/YYYY") : "-"}
                            </Typography>
                          </Stack>
                        </TableCell>
                        {/* Date with icon */}
                        <TableCell sx={{ border: "1px solid #e5e7eb" }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <AccessTimeOutlinedIcon
                              fontSize="small"
                              color="action"
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {new Date(
                                note.communicationDate
                              ).toLocaleString()}
                            </Typography>
                          </Stack>
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="center">
                          <Tooltip title="Edit Note">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleEditNote(note)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Note">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteNote(note)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                No notes available.
              </Typography>
            )}
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};

export default LeadNotesRow;
